import {
  activeSubscriptionSelector,
  desktopClientDownloadedSelector,
} from 'spa/selectors/users'
import { addAllTracksToCrate, removeAllTracks } from 'spa/action_creators/crate'
import { AVAILABLE_FEATURES, isFeatureEnabled } from 'spa/selectors/features'
import { batchDownload, throwDownloadError } from 'spa/action_creators/tracks'
import { ChangePropType } from 'spa/api/songs'
import { CrateAllTracksButton } from 'spa/components/track_buttons/crate_all_tracks_button'
import { defineMessages, injectIntl } from 'react-intl'
import { DownloadAllTracksButton } from 'spa/components/track_buttons/download_all_tracks_button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { isMobileSelector, isSafariSelector } from '../../selectors/device'
import { loggedInSelector } from 'spa/selectors/users'
import { MAX_DOWNLOAD_COUNT } from 'spa/shared/utils'
import { sendAllTracksToDownloader } from 'spa/action_creators/download_queue'
import { SendAllTracksToDownloaderButton } from 'spa/components/track_buttons/send_all_tracks_to_downloader_button'
import { TextStyle } from 'spa/components/typography'
import { tracksInDownloadQueue } from 'spa/selectors/songs'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import styles from './styles'

const localizedMessages = defineMessages({
  allVersions: {
    id: 'djcity.records.queue.track.all_tracks.title',
    defaultMessage: 'Add Versions',
  },
})

const TrackListItemAll = injectIntl(
  ({
    bold,
    className,
    color,
    fileDate,
    intl,
    songId,
    trackList,
    showFileDate = true,
    showTrackLength = true,
  }) => {
    const dispatch = useDispatch()
    const songsById = useSelector(state => state.songs.songsById)
    const tracks = songsById[songId].types

    const activeSubscription = useSelector(activeSubscriptionSelector)
    const loggedIn = useSelector(loggedInSelector)
    const desktopClientDownloaded = useSelector(desktopClientDownloadedSelector)
    const isBulkDownloadEnabled = useSelector(state =>
      isFeatureEnabled(state.features, AVAILABLE_FEATURES.BULK_DOWNLOAD)
    )
    const isMobile = useSelector(isMobileSelector)
    const isSafari = useSelector(isSafariSelector)
    const isIosSafari = isSafari && isMobile

    const trackIds = tracks.map(track => parseInt(track.ttid))
    const tracksInQueue = useSelector(state =>
      tracksInDownloadQueue(state, { songId, trackIds })
    )
    const someVersionsReachedLimit =
      Array.isArray(tracks) &&
      tracks.some(({ downloadCount }) => downloadCount >= MAX_DOWNLOAD_COUNT)
    const someVersionsAreBeingDownloaded =
      Array.isArray(tracks) && tracks.some(({ isDownloading }) => isDownloading)
    const allVersionsDownloaded =
      Array.isArray(tracks) &&
      tracks.every(({ downloadCount }) => downloadCount >= 1)
    const allVersionsInCrate = tracks.every(track => track.inCrate)

    const crateAllEnabled =
      loggedIn &&
      !someVersionsReachedLimit &&
      !allVersionsInCrate &&
      !allVersionsDownloaded

    const crateIconClass = crateAllEnabled
      ? null
      : allVersionsInCrate
      ? styles.iconActive
      : styles.iconDisabled

    const downloadAllEnabled =
      loggedIn &&
      activeSubscription &&
      !someVersionsReachedLimit &&
      !someVersionsAreBeingDownloaded &&
      isBulkDownloadEnabled &&
      !isIosSafari

    const downloadAllIconClass = downloadAllEnabled ? null : styles.iconDisabled

    const downloaderEnabled =
      loggedIn &&
      activeSubscription &&
      desktopClientDownloaded &&
      !someVersionsReachedLimit

    const downloaderIconClass = !downloaderEnabled
      ? styles.iconDisabled
      : tracksInQueue
      ? styles.iconActive
      : null

    /**
     * Event handlers
     */
    const handleCrateAllClick = useCallback(() => {
      if (!loggedIn) {
        dispatch(throwDownloadError())
      } else {
        allVersionsInCrate
          ? dispatch(removeAllTracks({ songId }))
          : dispatch(addAllTracksToCrate({ songId, trackList }))
      }
    }, [allVersionsInCrate, loggedIn, songId, trackList, dispatch])

    const handleSendAllToDownloaderClick = useCallback(() => {
      dispatch(sendAllTracksToDownloader({ songId, trackList }))
    }, [songId, trackList])

    const handleDownloadAllClick = useCallback(async () => {
      dispatch(batchDownload({ songId, trackList }))
    }, [songId, trackList])

    return (
      <li className={classNames(styles.trackListItem, className)}>
        <div>
          {allVersionsDownloaded && (
            <FontAwesomeIcon className={styles.check} icon="check" />
          )}
        </div>
        <TextStyle
          className={styles.versionName}
          color={color}
          variant={bold ? 'extra-bold' : 'default'}
        >
          {intl.formatMessage(localizedMessages.allVersions)}
        </TextStyle>
        {showFileDate &&
          (fileDate ||
            (tracks.some(t => !!t.fileDate) && (
              <TextStyle
                className={styles.fileDate}
                color={color}
                variant="label"
              >
                -
              </TextStyle>
            )))}
        {showTrackLength && tracks.some(t => t.trackLength) && (
          <TextStyle className={styles.fileDate} color={color} variant="label">
            -
          </TextStyle>
        )}
        <DownloadAllTracksButton
          className={downloadAllIconClass}
          onClick={handleDownloadAllClick}
        />
        <CrateAllTracksButton
          iconClassName={crateIconClass}
          onClick={handleCrateAllClick}
        />
        <SendAllTracksToDownloaderButton
          iconClassName={downloaderIconClass}
          onClick={handleSendAllToDownloaderClick}
        />
      </li>
    )
  }
)

TrackListItemAll.propTypes = {
  change: ChangePropType,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  songId: PropTypes.number.isRequired,
  trackList: PropTypes.string,
  tracks: PropTypes.array.isRequired,
}

export { TrackListItemAll }
